// The Bio's career list (?live=1, Ben 2026-10-19 — Catalyst's "Appearance History", linked into the
// canvas): one short line per season, newest first — club, appearances, minutes; a move between
// clubs stands out. The last 5 seasons, the rest behind "Show all". Click a card for that season's detail (every
// competition) — it changes nothing else. Drag a card onto a module: it shows that player in that
// season (Matches, Shot / Pass / Heat Map, Passing Profile: that season in that league; a Stats tile
// or Radar of someone else: she joins it for that season, the year-on-year merge).

import { useState, type PointerEvent as ReactPointerEvent } from "react";
import { careerLookups, careerSeasons, loadPlayerCareer, type CareerSeason } from "../live/career.js";
import { liveApi } from "../live/api.js";
import { useLiveQuery } from "../live/query.js";
import { useLiveLeagueChoices } from "../hooks/useLeagueTable.js";
import { startPlayerTokenDragOnMove } from "../canvas/menu-drag.js";
import { setStickyPlayer } from "../canvas/sticky-player.js";
import type { PlayerId } from "../data/players.js";

/** Seasons listed before "Show all". */
const SHOWN = 5;

export function BioCareer({ playerId, all = false }: { playerId: PlayerId; /** Every season, no "Show all" (the Bio's Career screen). */ all?: boolean }) {
  const career = useLiveQuery(`career:${playerId}`, () => loadPlayerCareer(liveApi(), playerId))!;
  const leagues = useLiveLeagueChoices();
  const { leagueOf, countries } = careerLookups(leagues);
  const seasons = careerSeasons(career.rows, leagueOf, countries);
  const [open, setOpen] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);

  if (seasons.length === 0) return null;

  // A press: a click stays a click (detail); the drag starts only once the pointer moves.
  const onPress = (e: ReactPointerEvent<HTMLElement>, s: CareerSeason) => {
    if (window.innerWidth <= 768 || e.button !== 0) return;
    e.stopPropagation();
    // Every module's own season keys, so whichever it lands on opens on this season + league.
    startPlayerTokenDragOnMove(e.nativeEvent, () => setStickyPlayer(playerId), {
      _selectedSeason: s.seasonCode,
      _selectedSeasonPlayer: playerId,
      _eventMapPlayer: playerId,
      _eventMapSeason: s.seasonCode,
      _eventMapCompetitionId: String(s.main.competitionId),
    });
  };

  const intl = careerSeasons(career.rows, leagueOf, countries, true).reverse();
  // Newest first; the last few seasons, the rest one click away (Ben, 2026-10-19: no sideways
  // scrolling through big cards — a compact list, no country or league).
  const newestFirst = [...seasons].reverse();
  const rows = all || showAll ? newestFirst : newestFirst.slice(0, SHOWN);
  const line = (s: CareerSeason) => (
    <div key={s.seasonId}>
      <div
        className={`career-row${s.seasonId === open ? " on" : ""}`}
        data-tooltip="Click for every competition · drag onto a module for this season"
        onClick={() => setOpen((o) => (o === s.seasonId ? null : s.seasonId))}
        onPointerDown={(e) => onPress(e, s)}
      >
        <span className="career-season">{s.label}</span>
        <span className="career-club">{s.main.teamName}</span>
        <span className="career-apps">
          {s.appearances} apps · {s.minutes.toLocaleString()}′
        </span>
      </div>
      {s.seasonId === open && (
        <div className="career-detail">
          {s.lines.map((l) => (
            <div className="career-line" key={`${l.competitionId}:${l.teamId}`}>
              <span className="career-line-league">{l.league?.label ?? `Competition ${l.competitionId}`}</span>
              <span className="career-line-club">{l.teamName}</span>
              <span className="career-line-num">
                {l.appearances} apps · {l.minutes.toLocaleString()}′
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
  return (
    <div className="bio-career">
      <div className="bio-career-label">Career</div>
      <div className="career-list">{rows.map(line)}</div>
      {!all && newestFirst.length > SHOWN && (
        <button type="button" className="career-more" onClick={() => setShowAll((v) => !v)}>
          {showAll ? "Show fewer" : `Show all ${newestFirst.length} seasons`}
        </button>
      )}
      {/* National teams, kept apart: filed under years ("2026"), and only some of them are in our
          data — said so, so the low numbers don't read as a bug (Ben, 2026-10-19). */}
      {intl.length > 0 && (
        <>
          <div className="bio-career-label career-intl-label">International</div>
          <div className="career-note">Only the international games in our data</div>
          <div className="career-list">{intl.map(line)}</div>
        </>
      )}
    </div>
  );
}
